import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { LengthUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  AxisKind,
  OutboundCubicSizeByCountryRateTable,
  OutboundCubicSizeByZoneRateTable,
  OutboundRateTableType,
  OutboundWeightByCountryRateTable,
  OutboundWeightByZoneRateTable,
  RateTableType,
  RateTableValueMode,
  WeightUnit,
} from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import { RateTableServiceLevel } from "@redotech/redo-fulfillment-rate-table-common/rate-table-mapping";

export const DEFAULT_INTERNATIONAL_COUNTRY_COLUMNS = [
  "CA",
  "GB",
  "FR",
  "DE",
  "AU",
  "NZ",
];

const DEFAULT_ZONE_COLUMNS = ["1", "2", "3", "4", "5", "6", "7", "8"];

const DEFAULT_WEIGHT_BANDS = [
  { minWeight: 0, maxWeight: 0.25 },
  { minWeight: 0.25, maxWeight: 0.5 },
  { minWeight: 0.5, maxWeight: 0.75 },
  { minWeight: 0.75, maxWeight: 0.99 },
  { minWeight: 0.99, maxWeight: 1 },
  { minWeight: 1, maxWeight: 2 },
  { minWeight: 2, maxWeight: 3 },
  { minWeight: 3, maxWeight: 4 },
  { minWeight: 4, maxWeight: 5 },
  { minWeight: 5, maxWeight: 6 },
  { minWeight: 6, maxWeight: 7 },
  { minWeight: 7, maxWeight: 8 },
  { minWeight: 8, maxWeight: 9 },
];

const DEFAULT_CUBIC_SIZE_BANDS = [
  { minCubicSize: 0, maxCubicSize: 0.1 },
  { minCubicSize: 0.1, maxCubicSize: 0.2 },
  { minCubicSize: 0.2, maxCubicSize: 0.3 },
  { minCubicSize: 0.3, maxCubicSize: 0.4 },
  { minCubicSize: 0.4, maxCubicSize: 0.5 },
  { minCubicSize: 0.5, maxCubicSize: 0.6 },
  { minCubicSize: 0.6, maxCubicSize: 0.7 },
  { minCubicSize: 0.7, maxCubicSize: 0.8 },
  { minCubicSize: 0.8, maxCubicSize: 0.9 },
  { minCubicSize: 0.9, maxCubicSize: 1 },
  { minCubicSize: 1, maxCubicSize: 2 },
  { minCubicSize: 2, maxCubicSize: 3 },
  { minCubicSize: 3, maxCubicSize: 4 },
  { minCubicSize: 4, maxCubicSize: 5 },
  { minCubicSize: 5, maxCubicSize: 6 },
  { minCubicSize: 6, maxCubicSize: 7 },
  { minCubicSize: 7, maxCubicSize: 8 },
  { minCubicSize: 8, maxCubicSize: 9 },
];

export function isCountryRateTableType(
  rateTableType: OutboundRateTableType,
): boolean {
  return (
    rateTableType === RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY ||
    rateTableType === RateTableType.OUTBOUND_SIZE_BY_COUNTRY
  );
}

export function isSizeRateTableType(
  rateTableType: OutboundRateTableType,
): boolean {
  return (
    rateTableType === RateTableType.OUTBOUND_SIZE_BY_ZONE ||
    rateTableType === RateTableType.OUTBOUND_SIZE_BY_COUNTRY
  );
}

const grid = (
  rowCount: number,
  columnCount: number,
  value: string,
): string[][] =>
  Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, () => value),
  );

export const defaultNewOutboundRateTable = (
  carrier: RateTableCarrier,
  serviceLevel: RateTableServiceLevel,
  valueMode: RateTableValueMode,
  rateTableType: OutboundRateTableType,
  provider: RateProvider,
):
  | OutboundWeightByZoneRateTable
  | OutboundCubicSizeByZoneRateTable
  | OutboundWeightByCountryRateTable
  | OutboundCubicSizeByCountryRateTable => {
  const baseTable = {
    valueMode,
    carrier,
    serviceLevel,
    provider,
    defaultValuePercentage: undefined,
    useDefaultValuePercentage: undefined,
  };

  const isCountry = isCountryRateTableType(rateTableType);
  const columnCount = isCountry
    ? DEFAULT_INTERNATIONAL_COUNTRY_COLUMNS.length + 1
    : DEFAULT_ZONE_COLUMNS.length;

  if (isSizeRateTableType(rateTableType)) {
    const data = grid(DEFAULT_CUBIC_SIZE_BANDS.length, columnCount, "0");
    const yAxis = {
      values: DEFAULT_CUBIC_SIZE_BANDS,
      kind: AxisKind.CUBIC_SIZE as const,
      units: LengthUnit.INCH,
    };
    return isCountry
      ? {
          ...baseTable,
          rateTableType: RateTableType.OUTBOUND_SIZE_BY_COUNTRY,
          data,
          xAxis: {
            values: [...DEFAULT_INTERNATIONAL_COUNTRY_COLUMNS],
            kind: AxisKind.COUNTRY,
          },
          yAxis,
          defaultValue: "0",
        }
      : {
          ...baseTable,
          rateTableType: RateTableType.OUTBOUND_SIZE_BY_ZONE,
          data,
          xAxis: { values: [...DEFAULT_ZONE_COLUMNS], kind: AxisKind.ZONE },
          yAxis,
          defaultValue: "0",
        };
  }

  const value = valueMode === RateTableValueMode.Rate ? "5" : "0.00";
  const data = grid(DEFAULT_WEIGHT_BANDS.length, columnCount, value);
  const yAxis = {
    values: DEFAULT_WEIGHT_BANDS,
    kind: AxisKind.WEIGHT as const,
    units: WeightUnit.LB,
  };
  return isCountry
    ? {
        ...baseTable,
        rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_COUNTRY,
        data,
        xAxis: {
          values: [...DEFAULT_INTERNATIONAL_COUNTRY_COLUMNS],
          kind: AxisKind.COUNTRY,
        },
        yAxis,
        defaultValue: "0.5",
      }
    : {
        ...baseTable,
        rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
        data,
        xAxis: { values: [...DEFAULT_ZONE_COLUMNS], kind: AxisKind.ZONE },
        yAxis,
        defaultValue: "0.5",
      };
};
